"use client";

import { useEffect, useState } from "react";
import Loading from "@/app/components/Loading";
import Gate, { GateHold, useGateHold } from "@/app/components/Gate";
import { Profile } from "./characterViews";

const WORLD = "Baunheim";

/** 辞書の項目。項目は後で増える */
type Book = "knowledge" | "battle";

const BOOKS: { book: Book; label: string; op: string }[] = [
  { book: "knowledge", label: "知見辞書", op: "dictionary" },
  { book: "battle", label: "戦闘辞書", op: "battle_dictionary" },
];

/** 知見辞書の 1 件 */
interface Entry {
  id: string;
  name: string;
  parts: { kind: string; body: string }[];
  updated: string | null;
}

/** 戦闘辞書の 1 項。body は名のほかが開いた項だけが持つ */
interface Monster {
  family: string;
  name: string;
  name_open: boolean;
  open: boolean;
  body: { look: string[]; habitat: string; weak: string[]; drops: string[]; star: number | null } | null;
}

/** 戦闘辞書の一族。all は全ての項の数、open は開いた項の数 */
interface Family {
  name: string;
  all: number;
  open: number;
}

interface BattleBook {
  families: Family[];
  entries: Monster[];
  count: { all: number; name_open: number; open: number };
}

interface Loaded {
  knowledge: Entry[] | null;
  battle: BattleBook | null;
}

const NONE: Record<Book, string> = { knowledge: "", battle: "" };

/** 戦闘辞書の一族の説明。一族を開いた画面の見出しの帯の中、名の下の段に出す */
const FAMILY_NOTE: Record<string, string> = {
  カラマゴ族: "卵の殻を苗床とする魔物。丸く、中はドロドロで、外側だけがやけに硬い殻に閉じこもっている。動く物に見境なく体当たりして作物と家畜を荒らし、毒や火を持つ者は家畜を弱らせて火事の元にもなる。",
  ヒヌケラ族: "蛇の抜け殻・蛹の殻・抜け毛・抜け羽のような抜け殻を苗床とする魔物。透けてひらひらした布のような体で宙を漂う。埃や灰や粉を撒いて家や仕事場を汚し、咳の病を広げたり峠道を凍えさせたりする。",
  カサカサ族: "枯れ葉・枝・切り株のような、もう栄養の通っていない植物を苗床とする魔物。乾いているのにうねうねと動く、枯れた植物の塊の姿をしている。林道や果樹園に潜んで人の足に絡み、棘で傷を負わせて魔力を奪う。",
  ダダンゴ族: "糞を苗床とする魔物。柔らかい塊がいくつも寄り集まった、臭いぼこぼこの山の姿をしている。叩くと潰れて飛び散り、浴びた者を臭いと汚れで病ませ、街に疫病を広げることもある。",
  グラッダン族: "鳥や獣や蜂や人間の作った巣を苗床とする魔物で、建物や乗り物もこれに入る。車ほどの大きさの巣の姿で、中の空洞に別の魔物を匿う。雨宿りや寝床を求めて近づいた人に噛みつき、匿った魔物を放って襲わせる。",
  ミミック族: "壺・樽・籠・箱のような、物を蓄える器を苗床とする魔物。器の中に黒いドロドロが詰まり、口を開けて中身を出す。器のまま蔵や市に紛れ、開けたり覗いたりした者に溜め込んだ中身を浴びせて襲う。",
  チャック族: "人の作った装備や道具を苗床とする魔物。苗床の道具を手に持った小人の姿をしている。畑や仕事場の跡に現れ、出会った人へ荒々しく道具を振るって襲いかかる。",
  ガギガギ族: "爪痕や掘った痕のような、何者かが意思を持って刻んだ跡を苗床とする魔物。刻まれた跡から立ちのぼるもやが本体で、幽霊のような体に鉤爪や釘を持つ。柱や木や壁に新しい傷を刻み続けて家や森を傷め、近づく者を爪で引き裂く。",
  デゾーモ族: "死骸を苗床とする魔物。ムカデのような本体が死骸の内側に棲み、外殻をまとって元の生き物を思わせる姿になる。畑と家畜を荒らして旅人を襲い、大きな者は村ひとつを脅かす。",
  フィジーデ族: "祠・祭壇・御神体・護符・人形・銅像のような、大勢の祈りや想いを注がれ続けた物を苗床とする魔物。目玉のような本体が必ず苗床の外に出ていて、苗床の物を自在に操る。祀られなくなった社や捨てられた人形から動き出し、近づく者や家の者に祟りをなす。",
};

/**
 * 辞書の画面。知見辞書と戦闘辞書の項目をタブで切り替える。
 * 知見辞書は、キャラが見て知ったことを、キャラの目で書き溜めた帳面。一覧は名だけを並べ、1 件を押すと本文を出す。
 * 同じ名の件は 1 件にまとめ、本文を書いた順に、元の種別の札を付けた畳みで並べる。元のエンティティは画面に出さない。
 * 戦闘辞書は、一族の並びを出す。一族を押すとその一族の説明と項の並びに移る。名のほかが開いた項だけを押せて、姿・生息地・弱点・落とす物・危なさを出す。
 * 中身の定めは Baunheim の WebUI の詳細設計書が持つ
 */
export default function PlayDictionary({ region, slug, version }: { region: string; slug: string; /** 進行が動いた印。変わったら読み直す */ version?: string }) {
  const [book, setBook] = useState<Book>("knowledge");
  const [loaded, setLoaded] = useState<Loaded>({ knowledge: null, battle: null });
  const [message, setMessage] = useState<Record<Book, string>>(NONE);
  const [opened, setOpened] = useState<{ book: Book; key: string } | null>(null);
  const [query, setQuery] = useState<Record<Book, string>>(NONE);
  /** 戦闘辞書で開いている一族の名 */
  const [family, setFamily] = useState<string | null>(null);

  // 開いている項目を読む。進行が動いたときと項目を替えたときは、出ている中身を残して裏で読み直す
  useEffect(() => {
    let alive = true;
    const op = BOOKS.find((one) => one.book === book)?.op ?? "dictionary";
    fetch(`/api/play/${op}?world=${WORLD}&region=${encodeURIComponent(region)}&slug=${encodeURIComponent(slug)}`)
      .then(async (response) => {
        const body = await response.json();
        if (!alive) return;
        if (response.ok) {
          setLoaded((old) => (book === "knowledge" ? { ...old, knowledge: (body.entries ?? []) as Entry[] } : { ...old, battle: body as BattleBook }));
          setMessage((old) => ({ ...old, [book]: "" }));
        } else setMessage((old) => ({ ...old, [book]: body.error ?? "辞書を読めません" }));
      })
      .catch(() => alive && setMessage((old) => ({ ...old, [book]: "サーバーに繋がりません" })));
    return () => {
      alive = false;
    };
  }, [book, region, slug, version]);

  // 中身を読んでいる最中。辞書の画面を開いたときは外のロード画面に、項目を替えたときは中のロード画面に待ってもらう
  const waiting = loaded[book] === null && message[book] === "";
  useGateHold(waiting);

  const entry = opened?.book === "knowledge" ? loaded.knowledge?.find((one) => one.id === opened.key) ?? null : null;
  const monster = opened?.book === "battle" ? loaded.battle?.entries.find((one, index) => String(index) === opened.key && one.body) ?? null : null;
  const kin = book === "battle" && family !== null ? loaded.battle?.families.find((one) => one.name === family) ?? null : null;

  if (entry) {
    return (
      <div>
        <div className="pl-title">
          <h2>{entry.name}</h2>
          <button type="button" className="pl-back" onClick={() => setOpened(null)}>
            戻る
          </button>
        </div>
        {entry.parts.length === 1 ? (
          <div className="pl-card">
            <Profile text={entry.parts[0].body} />
          </div>
        ) : (
          entry.parts.map((part, index) => (
            <details className="pl-card pl-fold" key={index}>
              <summary>{part.kind}</summary>
              <div className="pl-fold-body">
                <Profile text={part.body} />
              </div>
            </details>
          ))
        )}
      </div>
    );
  }

  if (monster?.body) {
    const body = monster.body;
    return (
      <div>
        <div className="pl-title">
          <h2>{monster.name}</h2>
          <span>{monster.family}</span>
          <button type="button" className="pl-back" onClick={() => setOpened(null)}>
            戻る
          </button>
        </div>
        <section className="pl-prof-sec">
          <h3>危なさ</h3>
          <p className="pl-prof-p pl-dict-star">{body.star === null ? "不明" : "★".repeat(body.star)}</p>
        </section>
        <section className="pl-prof-sec">
          <h3>姿</h3>
          {body.look.length > 0 ? (
            <ul className="pl-prof-items">
              {body.look.map((line, index) => (
                <li key={index}>{line}</li>
              ))}
            </ul>
          ) : (
            <p className="pl-prof-p">不明</p>
          )}
        </section>
        <section className="pl-prof-sec">
          <h3>生息地</h3>
          <p className="pl-prof-p">{body.habitat || "不明"}</p>
        </section>
        <section className="pl-prof-sec">
          <h3>弱点</h3>
          <p className="pl-prof-p">{body.weak.length > 0 ? body.weak.join("・") : "無し"}</p>
        </section>
        <section className="pl-prof-sec">
          <h3>落とす物</h3>
          {body.drops.length > 0 ? (
            <ul className="pl-prof-items">
              {body.drops.map((name, index) => (
                <li key={index}>{name}</li>
              ))}
            </ul>
          ) : (
            <p className="pl-prof-p">無し</p>
          )}
        </section>
      </div>
    );
  }

  const word = query[book].trim();

  if (kin && loaded.battle) {
    const shown = loaded.battle.entries.map((one, index) => ({ one, index })).filter(({ one }) => one.family === kin.name && (word === "" || one.name.includes(word)));
    return (
      <div>
        <div className="pl-title pl-dict-head">
          <h2>{kin.name}</h2>
          <span>
            開いた項 {kin.open}／{kin.all}
          </span>
          <button type="button" className="pl-back" onClick={() => setFamily(null)}>
            戻る
          </button>
          {FAMILY_NOTE[kin.name] ? <p className="pl-title-lead pl-dict-note">{FAMILY_NOTE[kin.name]}</p> : null}
          <input className="pl-search pl-title-search" type="search" placeholder="名で探す" value={query.battle} onChange={(e) => setQuery({ ...query, battle: e.target.value })} />
        </div>
        <ul className="pl-log-rows pl-dict-rows pl-card">
          {shown.map(({ one, index }) => (
            <li key={index}>
              <button
                type="button"
                className={one.open ? "pl-dict-item pl-dict-mon" : "pl-dict-item pl-dict-mon pl-dict-closed"}
                disabled={!one.open}
                onClick={() => setOpened({ book: "battle", key: String(index) })}
              >
                <span className="pl-dict-name">{one.name}</span>
                {one.body && one.body.star !== null ? <small className="pl-dict-star">★{one.body.star}</small> : null}
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  let content: React.ReactNode;
  if (message[book] && loaded[book] === null) {
    content = <p className="pl-message">{message[book]}</p>;
  } else if (book === "knowledge") {
    const entries = loaded.knowledge;
    if (entries === null) content = <Loading />;
    else if (entries.length === 0) content = <div className="pl-empty">まだ何も載っていません。</div>;
    else {
      const shown = entries.filter((one) => word === "" || one.name.includes(word));
      content = (
        <>
          <input className="pl-search" type="search" placeholder="名で探す" value={query.knowledge} onChange={(e) => setQuery({ ...query, knowledge: e.target.value })} />
          <ul className="pl-log-rows pl-dict-rows pl-card">
            {shown.map((one) => (
              <li key={one.id}>
                <button type="button" className="pl-dict-item" onClick={() => setOpened({ book: "knowledge", key: one.id })}>
                  {one.name}
                </button>
              </li>
            ))}
          </ul>
        </>
      );
    }
  } else {
    const battle = loaded.battle;
    if (battle === null) content = <Loading />;
    else if (battle.entries.length === 0) content = <div className="pl-empty">まだ何も載っていません。</div>;
    else {
      content = (
        <ul className="pl-log-rows pl-dict-rows pl-card">
          {battle.families.map((one) => (
            <li key={one.name}>
              <button
                type="button"
                className="pl-dict-item pl-dict-mon"
                onClick={() => {
                  setFamily(one.name);
                  setQuery({ ...query, battle: "" });
                }}
              >
                <span className="pl-dict-name">{one.name}</span>
                <small className="pl-dict-count">
                  {one.open}／{one.all}
                </small>
              </button>
            </li>
          ))}
        </ul>
      );
    }
  }

  const count = book === "knowledge" ? (loaded.knowledge ? `${loaded.knowledge.length} 件` : "") : loaded.battle ? `開いた項 ${loaded.battle.count.open}／${loaded.battle.count.all}` : "";

  return (
    <div className="pl-tabbed">
      <div className="pl-title">
        <h2>辞書</h2>
        <span>{count}</span>
      </div>
      <div className="pl-tabs pl-tabs-folder" role="tablist" aria-label="辞書の項目">
        {BOOKS.map((one) => (
          <button
            key={one.book}
            type="button"
            role="tab"
            aria-selected={book === one.book}
            className={book === one.book ? "pl-tab pl-tab-on" : "pl-tab"}
            onClick={() => setBook(one.book)}
          >
            {one.label}
          </button>
        ))}
      </div>
      <div className="pl-folder-body">
        {/* 項目を替えるたびに、その項目の中身が出せるまでロード画面を挟む */}
        <Gate key={book} variant="inline">
          <GateHold busy={waiting} />
          {content}
        </Gate>
      </div>
    </div>
  );
}
